/**
 * Appearance system: Light / Dark / System, plus a True Black (OLED) modifier.
 *
 * True Black is NOT a fourth mode: it is an independent boolean (the checkbox
 * under the theme segment) that upgrades the RESOLVED dark theme to the
 * pure-black 'oled' token layer in themes.css. It applies whenever the
 * effective theme is dark — Dark picked directly, or System resolving dark —
 * and never touches Light. An earlier design stored 'oled' as a single
 * appearance value; a stored 'oled' still boots black (getStoredTrueBlack
 * reads it) and initAppearance migrates it to dark + flag in place.
 *
 * SCOPE: the user-selectable appearance applies to the Script/Board/Timeline
 * workspace (?page=scene-board), the Projects home (?page=projects), the
 * Previs Assist side panel (?page=assist), since 20260928 the canvas overlay
 * (?page=canvas-overlay: the board's own Scene/Shot info, hamburger and
 * Preferences surfaces, shown over the 3D canvas) and, since 20261005, the canvas
 * Coverage overview (?page=canvas-coverage: the board's own Coverage overview in
 * the canvas's left side panel) — the five routes whose CSS has been audited for
 * light mode. Every OTHER route this SPA serves — purchasing, online
 * review, debug harness — and every other web view stays forced Dark: data-theme
 * lands on <html>, so without the route gate a stored 'light' would leak into
 * surfaces whose CSS was never audited for light mode.
 *
 * PERSISTENCE IS DUAL-LAYER (20260805). It used to be localStorage-only, which
 * made these the only two Preferences rows Unity could not restore: the board
 * webview's teardown calls UniWebView CleanCache(), and that drops
 * WKWebsiteDataTypeLocalStorage on the app-wide data store — so backing out of a
 * project deleted the keys and the next load booted the hardcoded defaults.
 *   - EMBEDDED: Unity is the authority. It appends ?appearance=&trueBlack= on
 *     every webview load (boot), ships both rows in the setPreferences catalog
 *     (applyAppearanceFromPreferences), and receives every change through the
 *     ordinary setPreference verb — exactly like the other Preferences rows.
 *   - STANDALONE / online review: no bridge exists, so localStorage remains the
 *     SOLE store and nothing about those modes changes.
 * localStorage is written in BOTH modes: embedded it is a best-effort mirror that
 * makes the pre-paint script (index.html) and the second same-origin document
 * agree, and losing it now costs a frame rather than the preference.
 *
 * Theme values live in src/styles/themes.css as :root[data-theme=...] token
 * overrides; the :root base in global.css IS the Dark theme. The page
 * background is also mirrored onto <html> style so the pre-React paint
 * (index.html hardcodes #111) cannot flash the wrong color mid-session; a
 * pre-paint inline script in index.html mirrors this module's storage read +
 * route gate so a light/oled boot doesn't flash dark either.
 */
export type Appearance = 'light' | 'dark' | 'system';
type ConcreteTheme = 'light' | 'dark' | 'oled';

/** localStorage keys — the index.html pre-paint script reads BOTH; keep in sync. */
export const APPEARANCE_STORAGE_KEY = 'timeline-appearance';
export const TRUE_BLACK_STORAGE_KEY = 'timeline-true-black';

/** Query params Unity appends to every embedded webview load — the boot half of native
 *  authority (the catalog cannot serve boot: it is only pushed on requestPreferences).
 *  The index.html pre-paint script duplicates these names; keep the two in sync. */
export const APPEARANCE_PARAM = 'appearance';
export const TRUE_BLACK_PARAM = 'trueBlack';

const PAGE_BACKGROUND: Record<ConcreteTheme, string> = {
  dark: '#111111',
  light: '#FAF7F5',
  oled: '#000000',
};

let systemMedia: MediaQueryList | null = null;
let systemListener: ((e: { matches: boolean }) => void) | null = null;

/** The stored preference (what the toggle shows), NOT the resolved concrete theme. */
let currentAppearance: Appearance = 'dark';
/** The True Black modifier — dark-effective themes render 'oled' while set. */
let trueBlackEnabled = false;
const listeners = new Set<() => void>();

/** Routes whose CSS is light-audited — see the header comment. The index.html
 *  pre-paint script duplicates this list; keep the two in sync. */
const THEMED_ROUTES = ['scene-board', 'projects', 'assist', 'canvas-overlay', 'canvas-coverage'];

function isThemedRoute(): boolean {
  try {
    const page = new URLSearchParams(window.location.search).get('page');
    return page !== null && THEMED_ROUTES.includes(page);
  } catch {
    return false;
  }
}

function applyConcrete(theme: ConcreteTheme): void {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.background = PAGE_BACKGROUND[theme];
}

/** The concrete theme for a dark-effective appearance — 'oled' while True Black is on.
 *  Read at apply/flip time (not captured), so a toggle mid-session re-resolves. */
function darkConcrete(): ConcreteTheme {
  return trueBlackEnabled ? 'oled' : 'dark';
}

function clearSystemListener(): void {
  if (systemMedia && systemListener) {
    systemMedia.removeEventListener('change', systemListener as unknown as EventListener);
    systemMedia = null;
    systemListener = null;
  }
}

export function applyAppearance(mode: Appearance): void {
  clearSystemListener();
  if (mode === 'system') {
    const matchMedia = (window as Window & typeof globalThis).matchMedia;
    if (typeof matchMedia === 'function') {
      systemMedia = matchMedia('(prefers-color-scheme: light)');
      systemListener = (e) => applyConcrete(e.matches ? 'light' : darkConcrete());
      systemMedia.addEventListener('change', systemListener as unknown as EventListener);
      applyConcrete(systemMedia.matches ? 'light' : darkConcrete());
      return;
    }
    applyConcrete(darkConcrete());
    return;
  }
  applyConcrete(mode === 'dark' ? darkConcrete() : mode);
}

export function getStoredAppearance(): Appearance {
  try {
    const v = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
    if (v === 'oled') return 'dark'; // legacy single-value True Black — see header
  } catch {
    /* storage unavailable (private mode, file:// quirks) — fall through */
  }
  return 'dark';
}

function getStoredTrueBlack(): boolean {
  try {
    // An explicit value always wins — the legacy fallback below must never override
    // a user's stored 'false' (a stale pre-change document can re-write 'oled' into
    // the appearance key post-boot, and refreshAppearanceFromStorage never migrates).
    const tb = localStorage.getItem(TRUE_BLACK_STORAGE_KEY);
    if (tb !== null) return tb === 'true';
    // Legacy single-value mode: 'oled' implied the modifier before it had its own key.
    return localStorage.getItem(APPEARANCE_STORAGE_KEY) === 'oled';
  } catch {
    return false;
  }
}

/** A recognised appearance, or null for "no answer" — an unset param, a value from a Unity
 *  build that predates these rows, or junk. Null NEVER means 'dark': callers keep what they
 *  have instead, so an older native side can't silently reset the user's theme. */
function coerceAppearance(value: unknown): Appearance | null {
  return value === 'light' || value === 'dark' || value === 'system' ? value : null;
}

/** Same contract for the modifier, read tolerantly across the three serializations in play:
 *  JSON bool (setPreferences catalog), '1'/'0' (URL param) and 'true'/'false' (the
 *  setPreference string convention + our own localStorage mirror). */
function coerceTrueBlack(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === '1' || value === 1) return true;
  if (value === 'false' || value === '0' || value === 0) return false;
  return null;
}

/** Best-effort re-seed of the localStorage mirror from live state. Called wherever native
 *  hands us an authoritative value, and on a wipe — see refreshAppearanceFromStorage. */
function writeMirror(appearance: Appearance, trueBlack: boolean): void {
  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, appearance);
    localStorage.setItem(TRUE_BLACK_STORAGE_KEY, String(trueBlack));
  } catch {
    /* storage unavailable (private mode, post-CleanCache) — the in-session value still applies */
  }
}

function readParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/**
 * Apply the theme rows of Unity's setPreferences catalog — the authoritative values, pushed
 * when the Preferences panel opens and re-pushed after every setPreference write.
 *
 * Each field is INDEPENDENT and unrecognised input is ignored rather than defaulted (see
 * coerceAppearance): a catalog from a Unity build without these rows leaves both untouched,
 * which is what keeps the web bundle safe to ship ahead of the native side.
 *
 * The mirror is re-seeded even when nothing changed — that repair is the whole point after a
 * CleanCache() wipe — but the document is only re-applied and subscribers only notified on an
 * actual change, so the catalog re-push that follows our own setPreference is a no-op.
 */
export function applyAppearanceFromPreferences(appearance: unknown, trueBlack: unknown): void {
  const nextAppearance = coerceAppearance(appearance) ?? currentAppearance;
  const nextTrueBlack = coerceTrueBlack(trueBlack) ?? trueBlackEnabled;
  writeMirror(nextAppearance, nextTrueBlack);
  if (nextAppearance === currentAppearance && nextTrueBlack === trueBlackEnabled) return;
  currentAppearance = nextAppearance;
  trueBlackEnabled = nextTrueBlack;
  if (isThemedRoute()) applyAppearance(nextAppearance);
  listeners.forEach((fn) => fn());
}

/** Current preference for UI (the Preferences theme segment). */
export function getAppearance(): Appearance {
  return currentAppearance;
}

/** Current True Black modifier for UI (the Preferences checkbox). */
export function getTrueBlack(): boolean {
  return trueBlackEnabled;
}

/** Subscribe to preference changes (useSyncExternalStore-shaped). */
export function subscribeAppearance(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * User-facing setter: persists, applies (themed route only — setting it from
 * the Preferences modal on a non-themed host like Projects home must not
 * restyle that host), and notifies subscribers.
 */
export function setAppearance(mode: Appearance): void {
  currentAppearance = mode;
  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, mode);
  } catch {
    /* best-effort — the in-session value still applies */
  }
  if (isThemedRoute()) applyAppearance(mode);
  listeners.forEach((fn) => fn());
}

/** True Black setter — same contract as setAppearance: persists, applies
 *  (themed route only), and notifies subscribers. */
export function setTrueBlack(enabled: boolean): void {
  trueBlackEnabled = enabled;
  try {
    localStorage.setItem(TRUE_BLACK_STORAGE_KEY, String(enabled));
  } catch {
    /* best-effort — the in-session value still applies */
  }
  if (isThemedRoute()) applyAppearance(currentAppearance);
  listeners.forEach((fn) => fn());
}

/**
 * Re-read the source of truth (localStorage) and reconcile. Needed because this
 * app runs TWO long-lived same-origin documents (the kept-alive Projects webview
 * is parked while the board runs, and restored WITHOUT a reload): a document's
 * boot-time cache can go stale when the OTHER document writes the preference.
 * Called from the cross-document 'storage' event (best-effort in WKWebView), on
 * visibility/focus recovery (the parked Projects document is restored in place —
 * nothing remounts, so this is what repaints it in the new theme), and on
 * useAppearance() mount (the Preferences modal mounts fresh on every open).
 */
export function refreshAppearanceFromStorage(): void {
  // BOTH keys gone = the store was WIPED, not a user reverting to defaults. Unity's board
  // teardown calls UniWebView CleanCache(), which clears WKWebsiteDataTypeLocalStorage on the
  // app-wide data store — so the parked Projects document used to come back on focus, read an
  // empty store, and downgrade a live light/true-black page to stock dark. Treat it as "no
  // answer" (the same rule as an absent preference elsewhere in this module): keep the live
  // state and re-seed the mirror from it. A key that IS present still wins, including a
  // deliberate 'dark'/'false' written by the other document.
  let appearanceRaw: string | null = null;
  let trueBlackRaw: string | null = null;
  let readable = true;
  try {
    appearanceRaw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    trueBlackRaw = localStorage.getItem(TRUE_BLACK_STORAGE_KEY);
  } catch {
    readable = false;
  }
  if (!readable || (appearanceRaw === null && trueBlackRaw === null)) {
    // Re-seed only when there is something to preserve. Sitting at the defaults there is nothing
    // to lose (a fresh boot resolves to exactly this), and skipping the write keeps standalone and
    // online review byte-identical to before: those modes have no bridge and never carried these
    // keys until the user actually picked something.
    if (currentAppearance !== 'dark' || trueBlackEnabled) {
      writeMirror(currentAppearance, trueBlackEnabled);
    }
    return;
  }

  // BOTH, not either: a store with exactly ONE key present is authoritative here, and that is
  // deliberate even though absence means "no answer" everywhere else in this module. setAppearance
  // and setTrueBlack write their keys INDEPENDENTLY, so a lone key whose sibling was never written
  // is an ordinary everyday shape — the user only ever touched one of the two controls — and the
  // missing sibling genuinely has no value beyond its default. It is not evidence of a partial
  // wipe: CleanCache() clears the whole data store at once, which is exactly the all-or-nothing
  // case the branch above catches. Reading a lone key as "no answer" would instead ignore a real
  // preference the other document just wrote.
  const stored = getStoredAppearance();
  const storedTrueBlack = getStoredTrueBlack();
  if (stored === currentAppearance && storedTrueBlack === trueBlackEnabled) return;
  currentAppearance = stored;
  trueBlackEnabled = storedTrueBlack;
  if (isThemedRoute()) applyAppearance(stored);
  listeners.forEach((fn) => fn());
}

let reconcileListenersAttached = false;

/** Boot entry point — called once from main.tsx (tests call it repeatedly; the
 *  reconcile listeners attach only once). */
export function initAppearance(): void {
  // URL params FIRST: in embedded mode Unity appends them on every load, and they are the only
  // authoritative value available at boot (the setPreferences catalog is pull-only — it arrives
  // when the Preferences panel asks for it). Fall back to the localStorage mirror, then the
  // defaults. Each param is judged on its own; an absent or junk one leaves that field to
  // storage rather than forcing a default.
  const paramAppearance = coerceAppearance(readParam(APPEARANCE_PARAM));
  const paramTrueBlack = coerceTrueBlack(readParam(TRUE_BLACK_PARAM));
  currentAppearance = paramAppearance ?? getStoredAppearance();
  trueBlackEnabled = paramTrueBlack ?? getStoredTrueBlack();
  try {
    // Migrate the legacy single-value 'oled' mode in place so every same-origin
    // document (and the pre-paint script) reads one representation from here on.
    // Never clobber an explicit True Black value: 'oled' + stored 'false' means a
    // stale pre-change document re-wrote the appearance key AFTER the user opted out.
    if (localStorage.getItem(APPEARANCE_STORAGE_KEY) === 'oled') {
      localStorage.setItem(APPEARANCE_STORAGE_KEY, 'dark');
      if (localStorage.getItem(TRUE_BLACK_STORAGE_KEY) === null) {
        localStorage.setItem(TRUE_BLACK_STORAGE_KEY, 'true');
      }
    }
  } catch {
    /* storage unavailable — the in-session values read above still stand */
  }
  // Re-seed the mirror from whatever native just told us, so the pre-paint script and the OTHER
  // same-origin document (the kept-alive Projects webview) agree with Unity on the next load.
  // Deliberately AFTER the legacy migration above, which is keyed on the stored 'oled' value:
  // seeding first would erase that signal before it is read.
  if (paramAppearance !== null || paramTrueBlack !== null) {
    writeMirror(currentAppearance, trueBlackEnabled);
  }
  if (isThemedRoute()) {
    applyAppearance(currentAppearance);
  } else {
    // Non-audited routes stay the STOCK dark (#111): True Black is part of the
    // themed-workspace appearance, so the route gate covers it too.
    clearSystemListener();
    applyConcrete('dark');
  }
  if (!reconcileListenersAttached) {
    try {
      window.addEventListener('storage', (e) => {
        if (e.key === null || e.key === APPEARANCE_STORAGE_KEY || e.key === TRUE_BLACK_STORAGE_KEY) {
          refreshAppearanceFromStorage();
        }
      });
      // The parked Projects webview is restored in place (no reload, no remount),
      // so neither initAppearance nor useAppearance runs again — and WKWebView's
      // cross-document 'storage' delivery is unreliable. Re-reading on the way
      // back in is what makes a theme picked on the board show up here. Both
      // events: an embedded WKWebView can regain focus without a
      // visibilitychange (same pairing as audioProcessor's recovery listeners).
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') refreshAppearanceFromStorage();
      });
      window.addEventListener('focus', () => refreshAppearanceFromStorage());
      reconcileListenersAttached = true;
    } catch {
      /* non-browser environment */
    }
  }
}
